import { useState, useEffect, useCallback } from 'react'
import type { ShiftZReport } from '@core/services/sync/SyncService'
import { CLOUD_API, CLOUD_NOT_CONNECTED, deviceBadge } from './shared'
import { deviceLabel, shortId } from '@core/utils/deviceLabel'
import { btn, btnBase, btnSize, cx, hint, modalLg, overlay, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { money } from '@core/utils/format'
import { closeShiftBtn, errBanner, shiftTab, shiftTabActive, shiftTabInactive } from '@ui/admin'

import { httpErrorText } from '@core/utils/httpError'
import { Box, Dot, Power } from '@ui/icons'
interface CloudShift {
  id: string
  device_id: string
  /** Зона (рабочее место) смены — ключ для облачного Z по зоне. */
  zone_id: string | null
  shift_number: number
  cashier_name: string
  status: 'open' | 'closed'
  fiscalization_enabled: boolean
  initial_cash: string | number
  total_sales: string | number
  sales_count: number
  z_report_data: unknown
  opened_at: string
  closed_at: string | null
}

/** Облачная вскрытая упаковка из журнала контроля (маркированные весовые). */

export function ShiftsMonitorSection({
  token,
  refreshKey = 0
}: {
  token: string
  refreshKey?: number
}) {
  const [shifts, setShifts] = useState<CloudShift[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState<'open' | 'closed'>('open')
  const [zReport, setZReport] = useState<{
    shift: CloudShift
    /** Локальный Z-отчёт, зафиксированный кассой при закрытии смены. */
    data: unknown
    /** Агрегат облака по ЗОНЕ (фискал/отложенные/нефискальные). */
    agg: ShiftZReport | null
    aggErr: string | null
  } | null>(null)

  const reload = useCallback(async () => {
    // Без токена запрос гарантированно вернёт 401 — показываем понятное состояние.
    if (!token) {
      setShifts([])
      setErr(null)
      return
    }
    setErr(null)
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/sync/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setErr(httpErrorText('Загрузка смен', resp.status))
        return
      }
      const body = (await resp.json()) as { shifts: CloudShift[] }
      setShifts(Array.isArray(body.shifts) ? body.shifts : [])
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [token])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  const closeShiftRemote = async (id: string) => {
    setErr(null)
    try {
      const resp = await fetch(`${CLOUD_API}/sync/shifts/${id}/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setErr(httpErrorText('Закрытие смены', resp.status))
        return
      }
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  /**
   * Открыть Z-отчёт смены: показывает локальный Z кассы и ПОДТЯГИВАЕТ из облака
   * агрегат по зоне (фискал/отложенные/нефискальные + разбивка по устройствам).
   */
  const openZReport = async (shift: CloudShift) => {
    setZReport({ shift, data: shift.z_report_data, agg: null, aggErr: null })
    if (!shift.zone_id) return
    try {
      const resp = await fetch(
        `${CLOUD_API}/sync/shift-z?zone=${encodeURIComponent(shift.zone_id)}&shift_number=${shift.shift_number}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (!resp.ok) {
        setZReport((z) => (z ? { ...z, aggErr: httpErrorText('Свод по зоне', resp.status) } : z))
        return
      }
      const agg = (await resp.json()) as ShiftZReport
      setZReport((z) => (z ? { ...z, agg } : z))
    } catch (e: unknown) {
      setZReport((z) => (z ? { ...z, aggErr: e instanceof Error ? e.message : String(e) } : z))
    }
  }

  return (
    <div aria-busy={busy}>
      {err && <p className={errBanner}>{err}</p>}
      {!token && (
        <p className={hint}>{CLOUD_NOT_CONNECTED}. Журнал смен доступен после входа.</p>
      )}

      {token && (() => {
        const openShifts = shifts.filter((s) => s.status === 'open')
        const closedShifts = shifts.filter((s) => s.status === 'closed')
        const rows = tab === 'open' ? openShifts : closedShifts
        return (
          <>
            <div className="mb-2.5 flex gap-2">
              <button
                className={tab === 'open' ? cx(shiftTab, shiftTabActive) : cx(shiftTab, shiftTabInactive)}
                onClick={() => setTab('open')}
              >
                <Dot size={16} className="text-okDark" /> Открытые смены ({openShifts.length})
              </button>
              <button
                className={tab === 'closed' ? cx(shiftTab, shiftTabActive) : cx(shiftTab, shiftTabInactive)}
                onClick={() => setTab('closed')}
              >
                <Box size={16} /> Архив смен ({closedShifts.length})
              </button>
            </div>

            {rows.length === 0 ? (
              <p className={hint}>
                {tab === 'open'
                  ? 'Открытых смен нет.'
                  : 'Архив Z-отчётов пуст — закрытых смен ещё нет.'}
              </p>
            ) : (
              <div className={tableWrap}>
                <table className={table}>
                  <thead>
                    <tr>
                      <th className={th}>Устройство</th>
                      <th className={thNum}>Смена №</th>
                      <th className={th}>Кассир</th>
                      <th className={thNum}>Выручка</th>
                      <th className={th}>Открыта</th>
                      <th className={th}>Закрыта</th>
                      <th className={th}>{tab === 'open' ? 'Действие' : 'Z-отчёт'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const dev = deviceBadge(s.device_id)
                      return (
                        <tr key={s.id}>
                          <td className={td}>
                            <div className="font-semibold">{dev.label}</div>
                            <div className="text-micro text-muted">{dev.short}</div>
                          </td>
                          <td className={tdNum}>{s.shift_number}</td>
                          <td className={td}>{s.cashier_name}</td>
                          <td className={tdNum}>{money(Number(s.total_sales))}</td>
                          <td className={td}>{new Date(s.opened_at).toLocaleString('ru-RU')}</td>
                          <td className={td}>
                            {s.closed_at ? new Date(s.closed_at).toLocaleString('ru-RU') : '—'}
                          </td>
                          <td className={td}>
                            {s.status === 'open' ? (
                              <button
                                className={closeShiftBtn}
                                onClick={() => void closeShiftRemote(s.id)}
                              >
                                <Power size={16} /> Закрыть смену
                              </button>
                            ) : s.z_report_data != null || s.zone_id ? (
                              <button onClick={() => void openZReport(s)} className={cx(btnBase, btn.secondary, btnSize.sm)}>Z-отчёт</button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      })()}

      {zReport && (
        <div className={cx(overlay, 'z-modal')} onClick={() => setZReport(null)}>
          <div className={modalLg} onClick={(e) => e.stopPropagation()}>
            <h4 className="mt-0 text-base2 font-bold">
              Z-отчёт · смена №{zReport.shift.shift_number} · рабочее место{' '}
              {shortId(zReport.shift.zone_id) || '—'}
            </h4>
            {/* Свод по ЗОНЕ из облака (все устройства рабочего места). */}
            {zReport.agg ? (
              <>
                <h5 className="my-2 text-sm font-semibold">Свод по зоне (облако)</h5>
                <table className={table}>
                  <tbody>
                    <tr>
                      <td className={td}>Фискальная выручка</td>
                      <td className={tdNum}>{money(zReport.agg.fiscal_total)}</td>
                    </tr>
                    <tr>
                      <td className={td}>Отложенные (ждут ККТ)</td>
                      <td className={tdNum}>{money(zReport.agg.deferred_total)}</td>
                    </tr>
                    <tr>
                      <td className={td}>Нефискальные (намеренно)</td>
                      <td className={tdNum}>{money(zReport.agg.non_fiscal_total)}</td>
                    </tr>
                    <tr>
                      <td className={cx(td, 'font-bold')}>Итого по ящику</td>
                      <td className={cx(tdNum, 'font-bold')}>
                        {money(zReport.agg.total_in_drawer)}
                      </td>
                    </tr>
                    {(zReport.agg.corrections_count ?? 0) > 0 &&
                      zReport.agg.total_with_corrections != null && (
                        <tr>
                          <td className={cx(td, 'font-bold')}>Итого с коррекциями</td>
                          <td className={cx(tdNum, 'font-bold')}>
                            {money(zReport.agg.total_with_corrections)}
                          </td>
                        </tr>
                      )}
                    <tr>
                      <td className={td}>Чеков за смену (зона)</td>
                      <td className={tdNum}>{zReport.agg.sales_count}</td>
                    </tr>
                    {/* Возвраты (ARCH-TASK-65): в выручке уже учтены со знаком «−». */}
                    {(zReport.agg.returns_count ?? 0) > 0 && (
                      <tr>
                        <td className={td}>Возвраты</td>
                        <td className={cx(tdNum, 'text-dangerDark')}>
                          {zReport.agg.returns_count} на {money(zReport.agg.returns_total ?? 0)}
                        </td>
                      </tr>
                    )}
                    {(zReport.agg.corrections_count ?? 0) > 0 && (
                      <tr>
                        <td className={td}>Коррекции прихода</td>
                        <td className={tdNum}>
                          {zReport.agg.corrections_income_count ?? 0} на{' '}
                          {money(zReport.agg.corrections_income_total ?? 0)}
                        </td>
                      </tr>
                    )}
                    {(zReport.agg.corrections_returns_count ?? 0) > 0 && (
                      <tr>
                        <td className={td}>Коррекции-возвраты</td>
                        <td className={cx(tdNum, 'text-dangerDark')}>
                          {zReport.agg.corrections_returns_count} на{' '}
                          {money(zReport.agg.corrections_returns_total ?? 0)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <h5 className="mb-1.5 mt-3 text-sm font-semibold">По устройствам</h5>
                <div className={tableWrap}>
                  <table className={table}>
                    <thead>
                      <tr>
                        <th className={th}>Устройство</th>
                        <th className={thNum}>Сумма</th>
                        <th className={thNum}>Чеков</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zReport.agg.by_device.map((d) => (
                        <tr key={d.device_id}>
                          <td className={cx(td, 'whitespace-nowrap')}>
                            {deviceLabel(d.device_id).label}
                          </td>
                          <td className={tdNum}>{money(d.total)}</td>
                          <td className={tdNum}>{d.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {zReport.agg.by_cashier.length > 0 && (
                  <>
                    <h5 className="mb-1.5 mt-3 text-sm font-semibold">По кассирам</h5>
                    <div className={tableWrap}>
                      <table className={table}>
                        <thead>
                          <tr>
                            <th className={th}>Кассир</th>
                            <th className={thNum}>Сумма</th>
                            <th className={thNum}>Чеков</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zReport.agg.by_cashier.map((c, i) => (
                            <tr key={`${c.cashier_name ?? 'none'}-${i}`}>
                              <td className={td}>{c.cashier_name || 'Без кассира'}</td>
                              <td className={tdNum}>{money(c.total)}</td>
                              <td className={tdNum}>{c.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : zReport.aggErr ? (
              <p className={errBanner}>Не удалось получить свод по зоне: {zReport.aggErr}</p>
            ) : (
              <p className={hint}>Загружаем свод по зоне…</p>
            )}

            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-semibold text-muted">
                Технические данные Z-отчёта
              </summary>
              <pre className="mt-1.5 whitespace-pre-wrap break-words font-mono text-small text-ink">{JSON.stringify(zReport.data, null, 2)}</pre>
            </details>
            <button onClick={() => setZReport(null)} className={cx(btnBase, btn.secondary, btnSize.sm)}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Контроль вскрытых упаковок (маркированные весовые товары).
 * GET /sync/containers: остаток массы, время вскрытия, касса, статус;
 * активные упаковки старше 72 ч подсвечиваются как просроченные.
 */
